import React, { useState, useEffect, useMemo } from "react";
import { RouteComponentProps, Link } from "react-router-dom";
import { api } from "../api";
import { useUser } from "../state";
import { SyncListenJSON } from "../../../src/apidefs";
import { EntryJSON } from "../models/entries";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  MusicalNoteIcon,
} from "@heroicons/react/24/outline";
import { Button } from "../DesignSystem/Button";

type MatchResult = {
  line: string;
  lineNumber: number;
  entry: EntryJSON | null;
  matchType: "exact" | "fuzzy" | "none";
};

function normalizeTitle(title: string): string {
  return title.toLowerCase().trim();
}

function findBestMatch(
  line: string,
  entries: EntryJSON[]
): { entry: EntryJSON | null; matchType: "exact" | "fuzzy" | "none" } {
  const normalizedLine = normalizeTitle(line);

  if (!normalizedLine) {
    return { entry: null, matchType: "none" };
  }

  // Try exact match first
  const exactMatch = entries.find(
    (e) => normalizeTitle(e.title) === normalizedLine
  );
  if (exactMatch) {
    return { entry: exactMatch, matchType: "exact" };
  }

  // Try substring match (line contains entry title or vice versa)
  const substringMatches = entries.filter((e) => {
    const normalizedEntryTitle = normalizeTitle(e.title);
    return (
      normalizedEntryTitle.includes(normalizedLine) ||
      normalizedLine.includes(normalizedEntryTitle)
    );
  });

  if (substringMatches.length === 1) {
    return { entry: substringMatches[0], matchType: "fuzzy" };
  }

  // Try word-based matching - check if most words match
  const lineWords = normalizedLine.split(/\s+/).filter((w) => w.length > 2);
  if (lineWords.length > 0) {
    let bestMatch: EntryJSON | null = null;
    let bestScore = 0;

    for (const entry of entries) {
      const entryWords = normalizeTitle(entry.title)
        .split(/\s+/)
        .filter((w) => w.length > 2);
      const matchingWords = lineWords.filter((w) => entryWords.includes(w));
      const score = matchingWords.length / Math.max(lineWords.length, 1);

      if (score > bestScore && score >= 0.5) {
        bestScore = score;
        bestMatch = entry;
      }
    }

    if (bestMatch) {
      return { entry: bestMatch, matchType: "fuzzy" };
    }
  }

  return { entry: null, matchType: "none" };
}

// Line height in pixels - must match textarea
const LINE_HEIGHT = 24;

function TracklistEditorModal({
  isOpen,
  onClose,
  onSave,
  entries,
  titleText,
  setTitleText,
  isSaving,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (entryIds: number[]) => void;
  entries: EntryJSON[];
  titleText: string;
  setTitleText: (text: string) => void;
  isSaving: boolean;
}) {
  // Parse lines and create match results for each line (including empty ones for alignment)
  // Note: The same entry can be matched multiple times (duplicates allowed in playlist)
  const allLineResults = useMemo((): (MatchResult | null)[] => {
    const lines = titleText.split("\n");

    return lines.map((line, index) => {
      const trimmedLine = line.trim();

      // Empty lines return null but still take up space
      if (!trimmedLine) {
        return null;
      }

      // Find match among all entries (duplicates allowed)
      const { entry, matchType } = findBestMatch(trimmedLine, entries);

      return {
        line: trimmedLine,
        lineNumber: index + 1,
        entry,
        matchType,
      };
    });
  }, [titleText, entries]);

  const matchedEntries = allLineResults.filter(
    (r): r is MatchResult => r !== null && r.entry !== null
  );
  const matchedCount = matchedEntries.length;
  const unmatchedCount = allLineResults.filter(
    (r) => r !== null && r.entry === null
  ).length;

  const handleSave = () => {
    const entryIds = matchedEntries
      .map((r) => r.entry?.id)
      .filter((id): id is number => id !== undefined);
    onSave(entryIds);
  };

  if (!isOpen) return null;

  const lineCount = Math.max(titleText.split("\n").length, 30);
  const contentHeight = lineCount * LINE_HEIGHT + 32;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 mx-4 flex max-h-[90vh] w-full max-w-7xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              Edit Tracklist
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {entries.length} songs available.
              {matchedCount > 0 && (
                <span className="ml-3 text-emerald-600 dark:text-emerald-400">
                  {matchedCount} matched
                </span>
              )}
              {unmatchedCount > 0 && (
                <span className="ml-2 text-red-600 dark:text-red-400">
                  {unmatchedCount} unmatched
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="flex items-start gap-6">
            {/* Left side - Text area */}
            <div className="w-1/2">
              <h3 className="mb-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
                Song Titles (one per line)
              </h3>
              <textarea
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
                placeholder="Paste song titles here, one per line..."
                style={{
                  lineHeight: `${LINE_HEIGHT}px`,
                  height: `${contentHeight}px`,
                }}
                className="w-full rounded-lg border border-slate-300 bg-white p-4 font-mono text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-500"
              />
            </div>

            {/* Right side - Matched entries */}
            <div className="w-1/2">
              <h3 className="mb-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
                Matched Entries
              </h3>
              <div
                className="rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-sm dark:border-slate-600 dark:bg-slate-700/50"
                style={{ height: `${contentHeight}px` }}
              >
                {allLineResults.map((result, index) => (
                  <div
                    key={index}
                    style={{ height: `${LINE_HEIGHT}px` }}
                    className="flex items-center gap-2"
                  >
                    {result === null ? (
                      // Empty line placeholder
                      <span className="text-slate-300 dark:text-slate-600">
                        —
                      </span>
                    ) : result.entry ? (
                      <>
                        <CheckCircleIcon
                          className={`h-4 w-4 flex-shrink-0 ${
                            result.matchType === "exact"
                              ? "text-emerald-500"
                              : "text-amber-500"
                          }`}
                        />
                        <span
                          className={`truncate ${
                            result.matchType === "exact"
                              ? "text-slate-800 dark:text-white"
                              : "text-amber-700 dark:text-amber-300"
                          }`}
                        >
                          {result.entry.title}
                        </span>
                      </>
                    ) : (
                      <>
                        <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0 text-red-500" />
                        <span className="truncate text-red-600 dark:text-red-400">
                          No match: "{result.line}"
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
          <Button
            variant="text"
            onClick={() => setTitleText(entries.map((e) => e.title).join("\n"))}
          >
            Add all tracks
          </Button>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            disabled={matchedCount === 0}
          >
            Save Tracklist ({matchedCount} entries)
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SyncListenEditPage(
  props: RouteComponentProps<{ id: string }>
): JSX.Element {
  const user = useUser();
  const compoId = Number(props.match.params.id);
  const [syncListen, setSyncListen] = useState<SyncListenJSON | null>(null);
  const [entries, setEntries] = useState<EntryJSON[]>([]);
  const [savedEntryCount, setSavedEntryCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [titleText, setTitleText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [syncListenResult, entriesResult, savedEntriesResult] =
        await Promise.all([
          api.get<"/api/compo/:id/sync-listen">(
            `/api/compo/${compoId}/sync-listen`
          ),
          api.get<"/api/compo/:id/entries">(`/api/compo/${compoId}/entries`),
          api.get<"/api/compo/:id/sync-listen/entries">(
            `/api/compo/${compoId}/sync-listen/entries`
          ),
        ]);

      setLoading(false);

      if (!api.isToast(syncListenResult)) {
        setSyncListen(syncListenResult.syncListen);
      }

      let songEntries: EntryJSON[] = [];
      if (!api.isToast(entriesResult)) {
        // Filter to only song entries
        songEntries = entriesResult.filter((e) => e.type === "song");
        setEntries(songEntries);
      }

      // Load saved entries and reconstruct the title text
      if (!api.isToast(savedEntriesResult) && savedEntriesResult.entries.length > 0) {
        setSavedEntryCount(savedEntriesResult.entries.length);

        // Reconstruct title text from saved entry IDs
        const entryMap = new Map(songEntries.map((e) => [e.id, e]));
        const titles = savedEntriesResult.entries
          .map((se) => entryMap.get(se.entryId)?.title)
          .filter((t): t is string => t !== undefined);
        setTitleText(titles.join("\n"));
      }
    };

    fetchData();
  }, [compoId]);

  const handleSave = async (entryIds: number[]) => {
    setIsSaving(true);

    const result = await api.post<"/api/compo/:id/sync-listen/entries">(
      `/api/compo/${compoId}/sync-listen/entries`,
      { entryIds }
    );

    setIsSaving(false);

    if (result.type === "success") {
      setSavedEntryCount(entryIds.length);
    }

    setIsModalOpen(false);
  };


  if (user.type !== "logged-in" || !user.user.admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">
          You must be an admin to view this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!syncListen) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">
          Sync listen not found. Please create one from the admin page.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 pb-8 pt-8 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8">
          <Link
            to={`/compo/${compoId}/admin`}
            className="mb-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to admin
          </Link>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
            Edit Sync Listen
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Configure the sync listen session for this compo.
          </p>
        </div>

        {/* Tracklist Card */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full rounded-2xl border border-slate-200 bg-white p-6 text-left transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-white/10 dark:bg-slate-800 dark:hover:border-blue-500/50 dark:hover:bg-slate-700"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
              <MusicalNoteIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                Tracklist
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {savedEntryCount > 0
                  ? `${savedEntryCount} entries`
                  : "Click to create tracklist"}
              </p>
            </div>
          </div>
        </button>
      </div>

      <TracklistEditorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        entries={entries}
        titleText={titleText}
        setTitleText={setTitleText}
        isSaving={isSaving}
      />
    </div>
  );
}
